import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { formatBytes, uInt8ToBase64 } from '/imports/utils';
import { initiateAndroidUpload, AndroidClient } from '/imports/android';
import type { FileMeta } from '/imports/types';
import { css } from '/imports/ui/css';
import { Icon } from '/imports/ui/Icon';
import { isDragLeaving, isFileDrag } from '/imports/ui/utils';
import { finishFileUpload, uploadFileChunk, fileHref, uploadLimitBytes, executableUploadRefusal, startUploadMethodFor } from '/imports/api/files';
import { strings } from '/imports/strings';

const FilesInputStyled = styled.div`
  background: ${css.inputBackgroundColor};
  input {
    width: 100%;
    min-width: 20rem;
    ${css.mobileMedia} {
      min-width: 0;
    }
    background: ${css.inputBackgroundColor};
    border: ${css.inputBorder};
    border-radius: ${css.borderRadius};
    padding: 0.5rem 0.75rem;
    box-sizing: border-box;
    font-size: 1rem;
    font-family: ${css.fontFamily};
    display: none;
    &::placeholder {
      text-transform: capitalize;
    }
    &[type=file] {
      background: '#FFF';
      color: ${css.grey2};
    }
  }
  td {
    padding-bottom: 2rem;
    .file-type, .file-size {
      margin-left: 1rem;
    }
  }
  .remove-file {
    padding-left: 2rem;
    svg {
      cursor: pointer;
      * {
        stroke: '#FFF';
      }
      &:hover {
        * {
          stroke: ${css.primaryColor};
        }
      }
    }
  }
  &.dragging .target {
    border-color: ${css.primaryColor};
    background: ${css.lightBlue};
  }
  .target {
    border-radius: ${css.inputBorderRadius};
    border: 2px dashed ${css.grey4};
    padding: 1.5rem;
    text-align: center;
    cursor: pointer;
    &.has-files {
      padding: 0.25rem 1.5rem;
    }
    .icon {
      margin: auto;
      flex-direction: row;
    }
  }
`;

export interface FilesInputProps {
  className?: string;
  placeholder?: string;
  value?: FileMeta[];
  disabled?: boolean;
  accept?: string;
  maxSize?: number;          // byte ceiling for this surface; clamped server-side to maxUploadBytes
  onChange?(fileMetas: FileMeta[], e?: React.ChangeEvent<HTMLInputElement>): void;
}

/**
 * File picker with drag-and-drop, chunked upload, progress bars, and editable file names.
 */
export const FilesInput = (props: FilesInputProps) => {
  const { value } = props;
  const _value = value || [];
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgresses, setUploadProgresses] = useState<number[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // Bound to the whole component, not just the small target strip, so a drop anywhere over the list of
  // files already added counts too.
  const dragHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (props.disabled || !isFileDrag(e)) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragOver: (e: React.DragEvent) => {
      if (props.disabled || !isFileDrag(e)) return;
      e.preventDefault();
    },
    onDragLeave: (e: React.DragEvent) => {
      if (isDragLeaving(e)) setDragging(false);
    },
    onDrop: async (e: React.DragEvent) => {
      if (props.disabled || !isFileDrag(e)) return;
      e.preventDefault();
      setDragging(false);
      await handleFiles(Array.from(e.dataTransfer.files));
    },
  };
  /**
   * Uploads files chosen via the native file input.
   * @param e - input change event
   */
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length) await handleFiles(newFiles, e);
  };
  /**
   * Renames a file and notifies the parent.
   * @param fileMeta - the file metadata to rename
   * @param e - input change event
   */
  const handleNameChange = (fileMeta: FileMeta, e: React.ChangeEvent<HTMLInputElement>) => {
    fileMeta.name = e.target.value;
    props.onChange?.([..._value], e);
  };
  /**
   * Removes the file at the given index and notifies the parent.
   * @param index - index of the file to remove
   */
  const handleRemoveFile = (index: number) => {
    _value.splice(index, 1);
    props.onChange?.([..._value]);
  };
  /**
   * Streams and uploads files in base64 chunks, tracking per-file progress.
   * @param newFiles - files to upload
   * @param e - originating change event, if any
   */
  const handleFiles = async (newFiles: File[], e?: React.ChangeEvent<HTMLInputElement>) => {
    const limit = uploadLimitBytes(props.maxSize);
    const tooBig = newFiles.filter((f) => f.size > limit);
    tooBig.forEach((f) => toast.error(strings.FileTooLarge(f.name, formatBytes(f.size), formatBytes(limit))));
    newFiles = newFiles.filter((f) => f.size <= limit);
    // Programs are dropped with a message each; the rest of the batch still goes up.
    newFiles = newFiles.filter((f) => {
      const refusal = executableUploadRefusal(f, props.accept);
      if (refusal) toast.error(refusal);
      return !refusal;
    });
    if (!newFiles.length) return;
    const fileMetas: FileMeta[] = [];
    const progresses = new Array(newFiles.length).fill(0);
    setFiles([...files, ...newFiles]);
    setUploadProgresses(progresses);
    for (const [i, file] of newFiles.entries()) {
      const { name, type, size } = file;
      const token = await startUploadMethodFor(props.accept).call({ name, type, size, maxSize: props.maxSize });
      progresses[i] = 1;
      setUploadProgresses([...progresses]);
      const stream = file.stream() as any;
      const reader = stream.getReader();
      let uploadedBytes = 0;
      let read;
      let chunk;
      let fileMeta;
      while ((read = await reader.read())) {
        if (read.value) {
          chunk = uInt8ToBase64(read.value);
          await uploadFileChunk.call({ token, chunk });
          uploadedBytes += read.value.byteLength;
          progresses[i] = Math.round(uploadedBytes / size * 100);
          setUploadProgresses([...progresses]);
        }
        if (read.done) {
          fileMeta = await finishFileUpload.call(token);
          fileMetas[i] = fileMeta;
          progresses[i] = 0;
          setUploadProgresses([...progresses]);
          break;
        }
      }
      fileMetas && props.onChange?.([..._value, ...fileMetas], e);
    }
    if (fileInput.current) fileInput.current.value = '';
  };
  /**
   * Opens the native picker, or routes to the Android upload bridge when present.
   * @param e - mouse event from the input
   */
  const handleInputClick = (e?: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    if (AndroidClient) initiateAndroidUpload((file: File) => handleFiles([file]));
    else if (!e) fileInput.current?.click();
  };
  return (
    <FilesInputStyled className={`files-input ${props.className || ''}${dragging ? ' dragging' : ''}`} {...dragHandlers}>
      <table>
        <tbody>
          {
            value?.map((fileMeta, i) => {
              return (
                <tr key={i}>
                  <td><Thumb file={fileMeta} /></td>
                  <td>
                    <div className="file-name"><input value={fileMeta.name} onChange={(e) => handleNameChange(fileMeta, e)} /></div>
                    <div className="file-type">{fileMeta.type}</div>
                    <div className="file-size">{formatBytes(fileMeta.size)}</div>
                  </td>
                  <td className="remove-file">
                    <Icon icon="close" onClick={() => handleRemoveFile(i)} />
                  </td>
                </tr>
              );
            })
          }
        </tbody>
      </table>
      {
        files.length && files.map((file, i) => {
          if (!uploadProgresses[i]) return null;
          return <Progress key={i} progress={uploadProgresses[i]} file={file} />;
        }) || null
      }
      <input
        type="file"
        ref={fileInput}
        className={value ? 'hide' : ''}
        placeholder={props.placeholder || ''}
        multiple={true}
        accept={props.accept}
        disabled={props.disabled}
        onClick={(e) => handleInputClick(e)}
        onChange={(e) => handleChange(e)}
      />
      <div className={`target${value?.length ? ' has-files' : ''}`} onClick={() => fileInput.current?.click()}>
        <Icon icon="postAdd" size={40} />
        {dragging && <div>{strings.DropToUpload()}</div>}
      </div>
    </FilesInputStyled>
  );
};

const ThumbStyled = styled.div`
  text-align: center;
  padding-right: 2rem;
  img, video, iframe {
    max-height: 7rem;
    max-width: 7rem;
  }
`;

interface ThumbProps {
  file: FileMeta
}

/**
 * Renders a clickable thumbnail for a file based on its MIME type.
 */
const Thumb = (props: ThumbProps) => {
  const file = props.file;
  if (file.type.startsWith('image')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a href={fileHref(file)} target='_blank' rel="noreferrer">
          <img src={fileHref(file)} alt={file.name} />
        </a>
      </ThumbStyled>
    );
  }
  if (file.type.startsWith('video')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a href={fileHref(file)} target='_blank' rel="noreferrer">
          <video src={fileHref(file)} />
        </a>
      </ThumbStyled>
    );
  }
  if (file.type.startsWith('application/pdf')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a href={fileHref(file)} target='_blank' rel="noreferrer">
          <iframe src={fileHref(file)} />
        </a>
      </ThumbStyled>
    );
  }
  return (
    <ThumbStyled className="file-widget-thumb">
      <a href={fileHref(file)}>{file.name}</a>
    </ThumbStyled>
  );
};

const ProgressStyled = styled.div`
  .file-widget-progress-bar {
    height: 0.5rem;
    width: 0;
    transition: width 0.2s linear;
    background: ${css.primaryColor};
    margin-bottom: 1rem;
  }
`;

interface ProgressProps {
  progress: number;
  file: File;
}

/**
 * Renders an upload progress bar for a single file.
 */
const Progress = (props: ProgressProps) => {
  const progress = props.progress;
  const file = props.file;
  return (
    <ProgressStyled className='file-widget-progress-wrapper'>
      <div className='file-widget-progress-filename'>{file.name}</div>
      <div className='file-widget-progress-bar' style={{ width: `${progress}%` }} />
    </ProgressStyled>
  );
};